import { UploadFileError } from "@redotech/redo-model/upload-file";

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }
      const base64 = result.split(",")[1];
      if (!base64) {
        reject(new Error("Failed to read file"));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

/**
 * Convert a base64 `data:` URL (e.g. the `FileReader` result for a pasted or
 * dropped image) back into a `File` so it can be uploaded through the same path
 * as a file picked from disk. Returns `null` when the string is not a base64
 * `data:` URL.
 */
export function dataUrlToFile(dataUrl: string, filename: string): File | null {
  const match = /^data:([^;,]+);base64,([A-Za-z0-9+/=]+)$/.exec(dataUrl);
  if (!match) {
    return null;
  }
  const [, mimeType, base64] = match;
  if (mimeType === undefined || base64 === undefined) {
    return null;
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mimeType });
}

export function extractFileNameFromUrl(
  url: URL | undefined,
): string | undefined {
  if (!url) {
    return undefined;
  }
  const segment = url.pathname.split("/").pop() ?? "";
  try {
    return decodeURIComponent(segment) || undefined;
  } catch {
    return segment || undefined;
  }
}

/**
 * Helper function to extract error messages from upload errors
 */
export function extractUploadErrorMessage(error: unknown): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof error.response === "object" &&
    error.response !== null &&
    "data" in error.response &&
    typeof error.response.data === "object" &&
    error.response.data !== null &&
    "error" in error.response.data &&
    "errorMessage" in error.response.data &&
    typeof error.response.data.errorMessage === "string" &&
    error.response.data.error ===
      UploadFileError.FILE_SIZE_LARGER_THAN_TARGET_MAX_SIZE
  ) {
    return error.response.data.errorMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong uploading the file. Please try again.";
}
